import React, { useMemo, useState } from 'react';
import { useUIColors } from '../../hooks/useTheme';
import ChartOptionsMenu from './ChartOptionsMenu';
import ChartSeriesLegend from './ChartSeriesLegend';
import {
  discoverDimensionOptions,
  filterItemsByDimensions,
  isFilterVisible,
  toggleVisibilityKey,
  visibilityMapFromOptions,
} from './chartFilterUtils';
import {
  formatMoneyClp,
  inventoryTimeSeries,
  MATERIAL_FAMILIES,
} from './rawMaterialsDomain';

const PLOT_HEIGHT = 234;
const LEFT_PAD = 54;
const RIGHT_PAD = 68;
const TOP_PAD = 12;
const BOTTOM_PAD = 28;
const SVG_WIDTH = 760;
const SVG_HEIGHT = PLOT_HEIGHT + TOP_PAD + BOTTOM_PAD;

const FAMILY_DIMENSION = {
  key: 'family',
  label: 'Categoría',
  order: MATERIAL_FAMILIES,
  fallback: 'Sin categoría',
};

const MATERIAL_DIMENSION = {
  key: 'material',
  label: 'Materia prima',
  fallback: 'Sin código',
};

const niceMax = (value) => {
  const n = Number(value) || 0;
  if (n <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(n));
  const scaled = n / exp;
  const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return nice * exp;
};

const formatKg = (value) => {
  const n = Number(value) || 0;
  return `${n.toLocaleString('es-CL', { maximumFractionDigits: n >= 100 ? 0 : 1 })} kg`;
};

const formatAxisMoney = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1e6) {
    return `$${(n / 1e6).toLocaleString('es-CL', { maximumFractionDigits: 1 })} M`;
  }
  if (Math.abs(n) >= 1e3) {
    return `$${(n / 1e3).toLocaleString('es-CL', { maximumFractionDigits: 0 })} mil`;
  }
  return formatMoneyClp(n);
};

const pointsFor = (rows, key, max, plotW, plotH) => {
  const last = Math.max(rows.length - 1, 1);
  return rows.map((row, index) => {
    const x = LEFT_PAD + (plotW * index) / last;
    const y = TOP_PAD + plotH - (Number(row[key] || 0) / max) * plotH;
    return { x, y, ...row };
  });
};

const polyline = (points) => points.map((point) => `${point.x},${point.y}`).join(' ');

const RawMaterialsInventoryChart = ({
  materials = [],
  lots = [],
  ingresos = [],
  egresos = [],
  catalogByCode = {},
}) => {
  const ui = useUIColors();
  const [dimensionVisibility, setDimensionVisibility] = useState({});
  const theme = ui.theme || {};
  const qtyColor = theme.chart?.primary || '#2563eb';
  const amountColor = theme.chart?.tertiary || '#16a34a';

  const filterItems = useMemo(
    () =>
      (materials || []).map((row) => ({
        ...row,
        family: row.family || 'Sin categoría',
        material: `${row.code} · ${row.name}`,
      })),
    [materials]
  );

  const filterDimensions = [FAMILY_DIMENSION, MATERIAL_DIMENSION];

  const filteredMaterials = useMemo(
    () => filterItemsByDimensions(filterItems, filterDimensions, dimensionVisibility),
    [filterItems, dimensionVisibility]
  );

  const rows = useMemo(
    () =>
      inventoryTimeSeries({
        materials,
        lots,
        ingresos,
        egresos,
        catalogByCode,
        selectedCodes: filteredMaterials.map((row) => row.code),
      }),
    [materials, lots, ingresos, egresos, catalogByCode, filteredMaterials]
  );

  const kgMax = niceMax(Math.max(0, ...rows.map((row) => row.kg)));
  const amountMax = niceMax(Math.max(0, ...rows.map((row) => row.amount)));
  const plotW = SVG_WIDTH - LEFT_PAD - RIGHT_PAD;
  const plotH = PLOT_HEIGHT;
  const kgPoints = pointsFor(rows, 'kg', kgMax, plotW, plotH);
  const amountPoints = pointsFor(rows, 'amount', amountMax, plotW, plotH);
  const latest = rows[rows.length - 1] || { kg: 0, amount: 0 };

  const familyOptions = useMemo(
    () => discoverDimensionOptions(filterItems, FAMILY_DIMENSION),
    [filterItems]
  );
  const materialOptions = useMemo(
    () => discoverDimensionOptions(filterItems, MATERIAL_DIMENSION),
    [filterItems]
  );

  const filterSections = [
    {
      key: 'family',
      title: 'Categoría',
      options: familyOptions,
      visibility: dimensionVisibility.family || {},
      onToggle: (key) =>
        setDimensionVisibility((prev) => ({
          ...prev,
          family: toggleVisibilityKey(prev.family || {}, key),
        })),
      onShowAll: () =>
        setDimensionVisibility((prev) => ({
          ...prev,
          family: visibilityMapFromOptions(familyOptions, true),
        })),
      onHideAll: () =>
        setDimensionVisibility((prev) => ({
          ...prev,
          family: visibilityMapFromOptions(familyOptions, false),
        })),
    },
    {
      key: 'material',
      title: 'Materias primas',
      options: materialOptions,
      visibility: dimensionVisibility.material || {},
      onToggle: (key) =>
        setDimensionVisibility((prev) => ({
          ...prev,
          material: toggleVisibilityKey(prev.material || {}, key),
        })),
      onShowAll: () =>
        setDimensionVisibility((prev) => ({
          ...prev,
          material: visibilityMapFromOptions(materialOptions, true),
        })),
      onHideAll: () =>
        setDimensionVisibility((prev) => ({
          ...prev,
          material: visibilityMapFromOptions(materialOptions, false),
        })),
    },
  ];

  const ticks = [0, 1, 2, 3, 4];
  const grid = ui.border;
  const muted = ui.muted;
  const text = ui.text;

  return (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: 10,
        overflow: 'visible',
        margin: '0 0 12px',
        background: ui.panel,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          padding: '7px 10px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          borderRadius: '10px 10px 0 0',
        }}
      >
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <ChartOptionsMenu
            axisModes={[]}
            dimensions={[]}
            metrics={[]}
            filterSections={filterSections}
          />
          <h3 style={{ margin: 0, fontSize: 13, color: text, fontWeight: 700, lineHeight: 1.2 }}>
            Inventario de materias primas
          </h3>
        </div>
        <span style={{ fontSize: 11, color: muted, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {formatKg(latest.kg)} · {formatMoneyClp(latest.amount)}
        </span>
      </div>
      <ChartSeriesLegend
        series={['Cantidad (kg)', 'Valor inventario']}
        colors={{ 'Cantidad (kg)': qtyColor, 'Valor inventario': amountColor }}
      />
      <div style={{ overflowX: 'auto', padding: '4px 8px 8px' }}>
        <svg viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} width="100%" height={SVG_HEIGHT} role="img">
          {ticks.map((tick) => {
            const y = TOP_PAD + (plotH * tick) / 4;
            const kgLabel = kgMax * (1 - tick / 4);
            const amountLabel = amountMax * (1 - tick / 4);
            return (
              <g key={`grid-${tick}`}>
                <line
                  x1={LEFT_PAD}
                  x2={LEFT_PAD + plotW}
                  y1={y}
                  y2={y}
                  stroke={grid}
                  strokeDasharray="3 4"
                />
                <text
                  x={LEFT_PAD - 8}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="10"
                  fill={qtyColor}
                >
                  {tick === 4 ? '0' : kgLabel >= 1000 ? `${Math.round(kgLabel / 100) / 10}k` : Math.round(kgLabel)}
                </text>
                <text
                  x={LEFT_PAD + plotW + 8}
                  y={y + 3}
                  textAnchor="start"
                  fontSize="10"
                  fill={amountColor}
                >
                  {formatAxisMoney(amountLabel)}
                </text>
              </g>
            );
          })}
          <line x1={LEFT_PAD} x2={LEFT_PAD} y1={TOP_PAD} y2={TOP_PAD + plotH} stroke={qtyColor} strokeWidth="1.5" />
          <line
            x1={LEFT_PAD + plotW}
            x2={LEFT_PAD + plotW}
            y1={TOP_PAD}
            y2={TOP_PAD + plotH}
            stroke={amountColor}
            strokeWidth="1.5"
          />
          <polyline
            fill="none"
            stroke={qtyColor}
            strokeWidth="2.2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={polyline(kgPoints)}
          />
          <polyline
            fill="none"
            stroke={amountColor}
            strokeWidth="2.2"
            strokeLinejoin="round"
            strokeLinecap="round"
            points={polyline(amountPoints)}
          />
          {kgPoints.map((point) => (
            <circle
              key={`kg-${point.key}`}
              cx={point.x}
              cy={point.y}
              r="3.2"
              fill={ui.panel}
              stroke={qtyColor}
              strokeWidth="1.8"
            >
              <title>{`${point.label}: ${formatKg(point.kg)}`}</title>
            </circle>
          ))}
          {amountPoints.map((point) => (
            <circle
              key={`amt-${point.key}`}
              cx={point.x}
              cy={point.y}
              r="3.2"
              fill={ui.panel}
              stroke={amountColor}
              strokeWidth="1.8"
            >
              <title>{`${point.label}: ${formatMoneyClp(point.amount)}`}</title>
            </circle>
          ))}
          {rows.map((row, index) => {
            const x = LEFT_PAD + (plotW * index) / Math.max(rows.length - 1, 1);
            return (
              <text
                key={`lbl-${row.key}`}
                x={x}
                y={TOP_PAD + plotH + 16}
                textAnchor="middle"
                fontSize="10"
                fill={muted}
              >
                {row.label}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default RawMaterialsInventoryChart;
